import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Factory, Mail, Phone, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useSortable } from "@/lib/sortable";
import { usePersistedState } from "@/lib/persist";
import { slaOverzichtNavOp } from "@/lib/overzichtNav";
import { LaadBalk, TabelLaadRij } from "@/components/LaadIndicator";

function NewLevDialog({ onCreated }) {
  const empty = { naam: "", email: "", telefoon: "", contactpersoon: "", plaats: "", notitie: "" };
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  useEffect(() => { if (open) setF(empty); }, [open]);
  const submit = async () => {
    if (!f.naam.trim()) { toast.error("Naam is verplicht"); return; }
    const { data } = await api.post("/leveranciers", f);
    toast.success("Aangemaakt"); setOpen(false); onCreated(data);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-lev-btn"><Plus className="h-4 w-4 mr-1" />Nieuwe leverancier</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nieuwe leverancier</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Naam *</Label><Input value={f.naam} onChange={(e)=>setF({...f,naam:e.target.value})} data-testid="lev-naam" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>E-mail</Label><Input value={f.email} onChange={(e)=>setF({...f,email:e.target.value})} data-testid="lev-email" /></div>
            <div><Label>Telefoon</Label><Input value={f.telefoon} onChange={(e)=>setF({...f,telefoon:e.target.value})} /></div>
          </div>
          <div><Label>Contactpersoon</Label><Input value={f.contactpersoon} onChange={(e)=>setF({...f,contactpersoon:e.target.value})} /></div>
          <div><Label>Plaats</Label><Input value={f.plaats} onChange={(e)=>setF({...f,plaats:e.target.value})} /></div>
          <div><Label>Notitie</Label><Textarea value={f.notitie} onChange={(e)=>setF({...f,notitie:e.target.value})} rows={3} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} data-testid="lev-submit">Aanmaken</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Leveranciers() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = usePersistedState("leveranciers:q", "");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leveranciers");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter(l => {
    if (!q) return true;
    const t = q.toLowerCase();
    return l.naam.toLowerCase().includes(t)
      || (l.email || "").toLowerCase().includes(t)
      || (l.contactpersoon || "").toLowerCase().includes(t)
      || (l.plaats || "").toLowerCase().includes(t);
  });
  const { sortedRows, SortHeader } = useSortable(filtered, "naam");
  const openLev = (lid) => {
    slaOverzichtNavOp("leveranciers", sortedRows.map(l => l.id), window.location.pathname + window.location.search);
    navigate(`/leveranciers/${lid}`);
  };

  return (
    <div className="space-y-6" data-testid="leveranciers-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm text-muted-foreground">Inkoop</div>
          <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Leveranciers</h1>
          <p className="text-sm text-muted-foreground mt-1">{filtered.length} leverancier(s)</p>
        </div>
        <NewLevDialog onCreated={(l)=>{ load(); navigate(`/leveranciers/${l.id}`); }} />
      </div>

      {loading && <LaadBalk label="Leveranciers laden" />}

      <div className="bg-card border border-border rounded-xl p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Zoek op naam, contact, plaats…" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="lev-search" />
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wide">
            <tr>
              <SortHeader k="naam">Naam</SortHeader>
              <SortHeader k="contactpersoon">Contact</SortHeader>
              <SortHeader k="email">E-mail</SortHeader>
              <SortHeader k="telefoon">Telefoon</SortHeader>
              <SortHeader k="plaats">Plaats</SortHeader>
              <SortHeader k="aantal_producten" align="right">Producten</SortHeader>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedRows.map(l => (
              <tr key={l.id} onClick={()=>openLev(l.id)}
                  className="hover:bg-secondary/40 cursor-pointer transition-colors"
                  data-testid={`lev-row-${l.id}`}>
                <td className="p-3 font-medium flex items-center gap-2">
                  <Factory className="h-4 w-4 text-primary" /> {l.naam}
                </td>
                <td className="p-3 text-muted-foreground">{l.contactpersoon || "—"}</td>
                <td className="p-3 text-muted-foreground" onClick={(e)=>e.stopPropagation()}>
                  {l.email
                    ? <a href={`mailto:${l.email}`} className="text-primary hover:underline inline-flex items-center gap-1"><Mail className="h-3 w-3" />{l.email}</a>
                    : "—"}
                </td>
                <td className="p-3 text-muted-foreground" onClick={(e)=>e.stopPropagation()}>
                  {l.telefoon
                    ? <a href={`tel:${l.telefoon.replace(/\s/g,"")}`} className="text-primary hover:underline inline-flex items-center gap-1"><Phone className="h-3 w-3" />{l.telefoon}</a>
                    : "—"}
                </td>
                <td className="p-3 text-muted-foreground">{l.plaats || "—"}</td>
                <td className="p-3 text-right text-muted-foreground">{l.aantal_producten || 0}</td>
                <td className="p-3 text-right text-muted-foreground"><ChevronRight className="h-4 w-4 inline" /></td>
              </tr>
            ))}
            {sortedRows.length === 0 && <TabelLaadRij colSpan={7} loading={loading} leeg="Nog geen leveranciers." />}
          </tbody>
        </table>
      </div>
    </div>
  );
}
